/**
 * Top navigation bar.
 *
 * NOTE on user-icon dropdown:
 * The form lives on the homepage now, so "Take Quiz" is gone. The top-right
 * user icon opens a dropdown with two entry points:
 *   • Dealers   → /dealer-auth (or /dealer if already logged in as dealer)
 *   • My Profile (matches)  → /my-quiz  (email-only login for returning users)
 * "My Profile" is hidden if the user is logged in as a dealer (they have their
 * own dashboard) and visible to everyone else.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, Store, LogOut, Truck, Moon, Sun, User, ChevronDown, Heart } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getAuthToken, clearAuthTokens } from '../utils/auth';
import BrandLogo from './BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const userMenuRef = useRef(null);

  // Track scroll position so we can switch the nav from transparent
  // (over the cinematic hero) to a solid glassmorphism backdrop after
  // the user scrolls past the headline.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  const checkAuth = async () => {
    const token = getAuthToken();
    if (token) {
      setIsLoggedIn(true);
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRole(response.data.role);
        if (response.data.dealer) setDealer(response.data.dealer);
      } catch (error) {
        clearAuthTokens();
        setIsLoggedIn(false);
      }
    } else {
      setIsLoggedIn(false);
      setUserRole(null);
      setDealer(null);
    }
  };

  const handleLogout = () => {
    clearAuthTokens();
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUserRole(null);
    setDealer(null);
    setUserMenuOpen(false);
    navigate('/');
    window.location.reload();
  };

  const isDealer = userRole === 'dealer' || userRole === 'super_admin' || dealer;

  // Hide navigation on standalone pages and dealer dashboard (but not dealer-auth)
  const standalonePages = ['/intake-form', '/trade-appraisal', '/rigready'];
  const isFreeToolPage = location.pathname.startsWith('/free/');
  const isEmbed = location.pathname.startsWith('/embed/');
  const hiddenOnDealerDashboard = location.pathname === '/dealer' || location.pathname.startsWith('/dealer?');
  // Buyer dashboard has its own chrome (sidebar + mobile top bar) — the global
  // fixed nav overlaps it on mobile, so hide it there (keep desktop behavior).
  const isBuyerDashboard = location.pathname.startsWith('/my-matches');
  if (standalonePages.includes(location.pathname) || hiddenOnDealerDashboard || isFreeToolPage || isEmbed) {
    return null;
  }

  const goToDealers = () => {
    setUserMenuOpen(false);
    setIsOpen(false);
    navigate(isLoggedIn && isDealer ? '/dealer' : '/dealer-auth');
  };

  const goToMyProfile = () => {
    setUserMenuOpen(false);
    setIsOpen(false);
    navigate('/my-quiz');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isBuyerDashboard ? 'hidden lg:block' : ''} ${
        scrolled
          ? 'bg-[#05070d]/85 backdrop-blur-xl border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.45)]'
          : 'bg-transparent border-b border-transparent'
      } text-white`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="group" data-testid="logo-link">
            <BrandLogo size="md" variant="wordmark" />
          </Link>

          {/* Desktop Right-side Controls */}
          <div className="hidden md:flex items-center space-x-1.5">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-md transition-colors"
              data-testid="theme-toggle-btn"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* User Icon with Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1.5 text-white/90 hover:text-white hover:bg-white/10 px-2.5 py-2 rounded-md text-sm font-medium transition-colors"
                data-testid="user-menu-button"
                aria-label="User menu"
              >
                <User size={18} />
                <ChevronDown size={13} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-60 rounded-xl shadow-2xl ring-1 ring-white/10 overflow-hidden z-50 bg-[#0c1224] border border-white/10 backdrop-blur-xl"
                  data-testid="user-menu-dropdown"
                >
                  {/* Dealers */}
                  <button
                    onClick={goToDealers}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-left text-white hover:bg-white/[0.06] transition-colors"
                    data-testid="user-menu-dealers"
                  >
                    <Truck size={16} className="text-cyan-300" />
                    <div className="flex-1">
                      <div>{isLoggedIn && isDealer ? 'My Dashboard' : 'Advisors'}</div>
                      <div className="text-xs font-normal text-slate-400">
                        {isLoggedIn && isDealer ? 'Manage your leads' : 'Sign in or join'}
                      </div>
                    </div>
                  </button>

                  {/* My Profile / Matches — for returning users (not dealers) */}
                  {!(isLoggedIn && isDealer) && (
                    <button
                      onClick={goToMyProfile}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-left text-white hover:bg-white/[0.06] transition-colors border-t border-white/10"
                      data-testid="user-menu-my-profile"
                    >
                      <Heart size={16} className="text-cyan-300" />
                      <div className="flex-1">
                        <div>My Profile</div>
                        <div className="text-xs font-normal text-slate-400">
                          View my matches
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Logout */}
                  {isLoggedIn && (
                    <button
                      onClick={handleLogout}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-left transition-colors border-t ${isDark ? 'text-red-400 hover:bg-gray-700 border-gray-700' : 'text-red-600 hover:bg-gray-50 border-gray-100'}`}
                      data-testid="user-menu-logout"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-white hover:bg-white/10"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-[#0c1224]/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-3 pt-3 pb-4 space-y-1">
            <button
              onClick={goToDealers}
              className="w-full text-left flex items-center gap-2.5 text-white/90 hover:text-white hover:bg-white/[0.06] px-3 py-3 rounded-lg text-base font-medium"
              data-testid="mobile-menu-dealers"
            >
              <Truck size={18} className="text-cyan-300" />
              {isLoggedIn && isDealer ? 'My Dashboard' : 'Dealers'}
            </button>

            {!(isLoggedIn && isDealer) && (
              <button
                onClick={goToMyProfile}
                className="w-full text-left flex items-center gap-2.5 text-white/90 hover:text-white hover:bg-white/[0.06] px-3 py-3 rounded-lg text-base font-medium"
                data-testid="mobile-menu-my-profile"
              >
                <Heart size={18} className="text-cyan-300" />
                My Profile
              </button>
            )}

            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-2.5 text-white/90 hover:text-white hover:bg-white/[0.06] px-3 py-3 rounded-lg text-base font-medium"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>

            {isLoggedIn && (
              <button
                onClick={() => { handleLogout(); setIsOpen(false); }}
                className="w-full text-left flex items-center gap-2.5 text-white/90 hover:text-white hover:bg-white/[0.06] px-3 py-3 rounded-lg text-base font-medium"
              >
                <LogOut size={18} className="text-rose-300" />
                Logout
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
